import {
  CategoryData,
  FooterSections,
  SubCategories,
  FlyoutTileItem,
  FlyoutConfig,
  MainTeaserConfig,
  SecondaryTeaserConfig,
  sanitizeFilePath,
  Facet,
  FacetValuesList,
} from '@packages/common';

export enum FastlaneNamespace {
  FASTLANE = 'fastlane',
  CONFIG = 'config',
  PACKED = 'packed',
}

type ErrorHandler = (error: any) => void;

export type CategoryInitial = {
  categoryInitial: CategoryData;
  subCategories: SubCategories;
};

const log = (isDev = false, label: string, message: string) => isDev && console.log(`${label}:`, message);

// TODO: public config
export const createFastlaneClient = (fastlaneUrl: string, targetEnv: string, onError: ErrorHandler = () => {}) => {
  const isDev = targetEnv !== 'production';

  const fastlaneFetch = async <T>(namespace: FastlaneNamespace, key: string): Promise<T> => {
    const fullUrl = `${fastlaneUrl}/${namespace}?k=${key}`;

    log(isDev, 'Fastlane fetch', fullUrl);
    // TODO: fix this.
    const res = await fetch(fullUrl)
      .then((res) => res.json())
      .catch(onError);

    return res;
  };

  const fetchCategoryInitial = async (
    categoryName: string,
    parentPath: string,
    withoutSubCategories = false,
  ): Promise<CategoryInitial> => {
    const [categoryInitial, subCategories] = await Promise.all([
      fastlaneFetch<CategoryData>(FastlaneNamespace.PACKED, `categoryinitial:${categoryName}`),
      fastlaneFetch<SubCategories>(FastlaneNamespace.FASTLANE, `nav:${categoryName}`),
    ]);

    if (withoutSubCategories) {
      return {
        categoryInitial,
        subCategories: { parents: [], items: [] },
      };
    }

    return {
      categoryInitial,
      subCategories:
        // TODO remove 'not-found' case somewhere after 09.2020
        subCategories !== ('not found' as any) && !subCategories?.error
          ? subCategories
          : await fastlaneFetch<SubCategories>(FastlaneNamespace.FASTLANE, `nav:${parentPath}`),
    };
  };

  const fetchFooterSections = async () => {
    const [main, follow] = await Promise.all([
      fastlaneFetch<FooterSections['main']>(FastlaneNamespace.CONFIG, 'api/v1/meta-nav/main.json'),
      fastlaneFetch<FooterSections['follow']>(FastlaneNamespace.CONFIG, 'api/v1/meta-nav/follow.json'),
    ]);
    return { main, follow };
  };

  const fetchFlyoutConfig = (categoryName: string) => {
    const path = sanitizeFilePath(`api/v1/flyout/${categoryName}/data.json`);
    return fastlaneFetch<FlyoutConfig>(FastlaneNamespace.CONFIG, path);
  };

  const fetchFlyoutTilesConfig = (categoryName: string) => {
    return fetchFlyoutConfig(categoryName).then((res) => {
      const flyoutTilesConfig: FlyoutTileItem[] | undefined =
        res && res.content && res.content[0] && res.content[0].props && res.content[0].props.items;
      return flyoutTilesConfig || [];
    });
  };

  const fetchSubCategories = (path: string) => {
    return fastlaneFetch<SubCategories>(FastlaneNamespace.FASTLANE, sanitizeFilePath(`nav:/${path}`));
  };

  const fetchMainTeaserConfig = (categoryUrl: string) => {
    const path = sanitizeFilePath(`api/v1/teaser/${categoryUrl}/main.json`);
    return fastlaneFetch<MainTeaserConfig>(FastlaneNamespace.CONFIG, path);
  };

  const fetchSecondaryTeaserConfig = (categoryUrl: string) => {
    const path = sanitizeFilePath(`api/v1/teaser/${categoryUrl}/secondary.json`);
    return fastlaneFetch<SecondaryTeaserConfig>(FastlaneNamespace.CONFIG, path);
  };

  const fetchInactiveFacets = (categoryUrl: string) => {
    return fastlaneFetch<Facet[]>(FastlaneNamespace.FASTLANE, `categoryloadinactivefacets:${categoryUrl}`);
  };
  const fetchFacetValues = (categoryUrl: string, facetSeoName: string) => {
    return fastlaneFetch<FacetValuesList>(
      FastlaneNamespace.FASTLANE,
      `categoryloadfacetvalues:${facetSeoName}:${categoryUrl}`,
    );
  };

  return {
    fetchCategoryInitial,
    fetchFooterSections,
    fetchFlyoutConfig,
    fetchFlyoutTilesConfig,
    fetchSubCategories,
    fetchMainTeaserConfig,
    fetchSecondaryTeaserConfig,
    fetchInactiveFacets,
    fetchFacetValues,
  };
};
